import { FiltersContext } from "@components/commerce/product-listing/filters/context/FiltersContext";
import { getSortByOptions } from "@components/commerce/product-listing/filters/toolbar/item/sortBy/SortBy";
import { activeSiteCurrency } from "@config/site/site-config";
import { useAlgoliaClient } from "@hooks/useAlgoliaClient";
import { AnalyticsClient } from "@lib/analytics/AnalyticsClient";
import { CommonCMS } from "@lib/constants/contentful";
import { useMicrocopy } from "@lib/contentful/microcopy/MicrocopyContext";
import { isShoeSize, makeNonShoeSizePrettySize } from "@lib/utils/productUtility";
import { scrollToTop } from "@lib/utils/scrollToTop";
import { useStore } from "@store/index";
import { Button } from "@ui/components/forms/button/Button";
import { Box } from "@ui/components/layout/box/Box";
import { ECCOIcon } from "@ui/components/media-and-icons/ecco-icon/ECCOIcon";
import { useContext, useEffect } from "react";
import { useCurrentRefinements, useSortBy } from "react-instantsearch";
import { useMoney } from "ui/src/components/data-display/money/useMoney";
import { styles } from "./SelectedFiltersStyles";

export const CLEAR_ALL_BUTTON_TEST_ID = "clear-all-button";
export const SELECTED_FILTERS_TEST_ID = "selected-filters";

export const SelectedFilters = ({ locale }) => {
    const { getMultiple } = useMicrocopy();
    const microcopies = getMultiple(CommonCMS.page, [
        CommonCMS.generic.true,
        CommonCMS.generic.false,
    ]);

    const { clearRefinements } = useContext(FiltersContext);
    const money = useMoney({
        money: {
            centAmount: 0,
            currencyCode: activeSiteCurrency,
            fractionDigits: 0,
        },
        locale,
        convertFromCents: true,
    });
    const { items, refine } = useCurrentRefinements();

    const { get: getMicrocopy } = useMicrocopy();

    useEffect(() => {
        const timeoutId = setTimeout(() => AnalyticsClient.filtersView(items), 1000);
        return () => clearTimeout(timeoutId);
    }, [items]);

    const setLastLoadedPage = useStore((state) => state.setLastLoadedPage);

    const callBackClearFillters = () => {
        setLastLoadedPage(1);
        clearRefinements();
        scrollToTop();
    };
    const { indexName } = useAlgoliaClient(locale);
    const { sortByOptionsList } = useContext(FiltersContext);

    const { currentRefinement } = useSortBy({
        items: getSortByOptions(indexName, sortByOptionsList),
    });

    const currentFilters = items.filter((it) => it.indexName === currentRefinement);

    return currentFilters.length ? (
        <Box sx={styles.selectedFiltersContainer} data-testid={SELECTED_FILTERS_TEST_ID}>
            {currentFilters.map((item) => {
                return item.refinements.map((it) => {
                    let filterLabel: string;

                    switch (item.label) {
                        case "priceRange":
                            filterLabel = `${money.currencyNarrowSymbol} ${it.label}`;
                            break;
                        case "dominantColor":
                            filterLabel = it.label;
                            break;
                        case "sizes":
                            filterLabel = isShoeSize(it.label)
                                ? it.label
                                : makeNonShoeSizePrettySize(it.label);
                            break;
                        default:
                            filterLabel =
                                it.label === "true"
                                    ? microcopies[CommonCMS.generic.true]
                                    : it.label === "false"
                                      ? microcopies[CommonCMS.generic.false]
                                      : it.label;
                    }

                    return (
                        <Button
                            key={it.value}
                            variant="tertiary"
                            size="md"
                            pl={6}
                            iconSpacing={0}
                            rightIcon={<ECCOIcon name="close" aria-label="Close" />}
                            onClick={() => {
                                setLastLoadedPage(1);
                                scrollToTop();
                                refine(it);
                            }}
                            sx={styles.selectedFiltersItem}
                        >
                            {filterLabel}
                        </Button>
                    );
                });
            })}
            <Button
                size={"sm"}
                variant={"outline"}
                colorScheme={"white"}
                onClick={() => {
                    callBackClearFillters();
                }}
                sx={styles.clearAllFiltersButton}
                data-testid={CLEAR_ALL_BUTTON_TEST_ID}
            >
                {getMicrocopy("page", "PLP.toolbar.clearAllFilters")}
            </Button>
        </Box>
    ) : null;
};

export default SelectedFilters;
